/**
 * CSV import/export tied to the user-editable wiseentry.csv template in the
 * app-config folder.
 *
 * wiseentry.csv defines column structure: the header row sets column order and
 * names; example rows below it are ignored on read but help the user
 * understand what each column is for.
 *
 * Known column names (case-insensitive — match column header to a known field):
 *   vendor_name, invoice_number, invoice_date, due_date,
 *   property_code, gl_code, description, amount, tax_amount,
 *   currency, notes
 *
 * Unknown columns get emitted empty for every row, allowing the user to add
 * static-value or post-processing columns without code changes.
 */

import Papa from 'papaparse';

export const DEFAULT_TEMPLATE_FILENAME = 'wiseentry.csv';

const KNOWN = {
  vendor_name:    'vendor_name',
  vendor:         'vendor_name',
  vendorname:     'vendor_name',
  invoice_number: 'invoice_number',
  invoiceno:      'invoice_number',
  invoice:        'invoice_number',
  invoice_date:   'invoice_date',
  invoicedate:    'invoice_date',
  date:           'invoice_date',
  due_date:       'due_date',
  duedate:        'due_date',
  property_code:  'property_code',
  property:       'property_code',
  gl_code:        'gl_code',
  glcode:         'gl_code',
  account_code:   'gl_code',
  account:        'gl_code',
  description:    'description',
  desc:           'description',
  amount:         'amount',
  total:          'amount',
  total_amount:   'amount',
  tax_amount:     'tax_amount',
  tax:            'tax_amount',
  currency:       'currency',
  notes:          'notes',
  note:           'notes',
  memo:           'notes'
};

function normalizeHeader(h) {
  return String(h || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/* -------- Default template -------- */

export const DEFAULT_TEMPLATE_CONTENTS = `vendor_name,invoice_number,invoice_date,due_date,property_code,gl_code,description,amount,tax_amount,currency,notes
Acme Plumbing LLC,INV-2026-0412,2026-04-15,2026-05-15,HOU-001,6200-REPAIRS,Emergency leak repair - unit 4B,1250.00,103.13,USD,
City Water Authority,WTR-99834,2026-04-01,2026-04-30,HOU-001,6300-UTILITIES,April water service,432.18,,USD,
FirePro Tech LLC,SI16405,2026-03-25,2026-04-24,HOU-001,6200-REPAIRS,Service Call to investigate Pt 164 - 3530 SUITE A2 POWER SUPPLY 3 Troubles,480.00,,USD,
FirePro Tech LLC,SI16405,2026-03-25,2026-04-24,HOU-001,6210-TRIPCHARGES,"Truck/Trip Charge, (Service Dept, Alarm & Detection)",75.00,,USD,
FirePro Tech LLC,SI16405,2026-03-25,2026-04-24,HOU-001,2200-SALESTAX,Sales Tax,39.60,,USD,
"Smith, Jones & Co",2026-Q2-887,2026-04-20,2026-05-20,HOU-002,6100-LEGAL,"Legal review, lease renewals",2400.00,,USD,Quarterly retainer
`;

/* -------- Read template from app-config folder -------- */

/**
 * Read wiseentry.csv from the app-config folder. If missing, create one with
 * defaults and return its parsed columns.
 */
export async function loadTemplate(appConfigHandle, filename = DEFAULT_TEMPLATE_FILENAME) {
  if (!appConfigHandle) throw new Error('App-config folder not set.');

  let fileHandle;
  try {
    fileHandle = await appConfigHandle.getFileHandle(filename);
  } catch {
    // Doesn't exist — create with defaults.
    fileHandle = await appConfigHandle.getFileHandle(filename, { create: true });
    const w = await fileHandle.createWritable();
    await w.write(DEFAULT_TEMPLATE_CONTENTS);
    await w.close();
    return parseTemplate(DEFAULT_TEMPLATE_CONTENTS, true);
  }

  const file = await fileHandle.getFile();
  const text = await file.text();
  return parseTemplate(text, false);
}

function parseTemplate(text, justCreated) {
  const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });
  if (parsed.errors && parsed.errors.length > 0 && parsed.data.length === 0) {
    throw new Error(`Could not parse wiseentry.csv: ${parsed.errors[0].message}`);
  }
  const headers = (parsed.data[0] || []).map(h => String(h));
  const columns = headers.map(h => ({
    rawHeader: h,
    field:     KNOWN[normalizeHeader(h)] || null   // null means "static / unknown — leave blank"
  }));
  return { columns, justCreated };
}

/* -------- Build CSV rows from extracted invoices -------- */

/**
 * Build a row object for one extracted+coded file using the template columns.
 *
 * @param {Object} columns - from loadTemplate(): [{ rawHeader, field }, …]
 * @param {Object} file    - file record with .extracted and .coding
 * @param {Object} settings - { dateFormat: 'YYYY-MM-DD' | 'MM/DD/YYYY' | 'DD/MM/YYYY' }
 */
function effectiveExtracted(file) {
  return { ...(file.extracted || {}), ...(file.edits || {}) };
}

function distLineKey(line) {
  if (line.kind === 'tax') return `t${line.taxIndex ?? 0}`;
  if (line.kind === 'item') return `i${line.lineIndex ?? 0}`;
  return 's0';
}

/** One CSV row (summary) — legacy helper; prefer buildRowsForFile. */
export function buildRow(columns, file, settings = {}) {
  const rows = buildRowsForFile(columns, file, settings);
  return rows[0] || {};
}

/**
 * One or more CSV rows per invoice (v0.3): summary = 1 row; detail = N rows.
 * @returns {Array<Record<string,string>>}
 */
export function buildRowsForFile(columns, file, settings = {}) {
  const e = effectiveExtracted(file);
  const c = file.coding || {};
  const fmt = settings.dateFormat || 'YYYY-MM-DD';
  const d = file.distribution;
  const edits = file.distributionEdits || {};

  const baseRow = () => {
    const row = {};
    for (const col of columns) {
      row[col.rawHeader] = '';
    }
    return row;
  };

  const fill = (row, field, val) => {
    for (const col of columns) {
      if (col.field === field) row[col.rawHeader] = val;
    }
  };

  if (!d || d.mode === 'summary' || !Array.isArray(d.lines) || d.lines.length === 0) {
    const row = baseRow();
    for (const col of columns) {
      const f = col.field;
      let v = '';
      switch (f) {
        case 'vendor_name':    v = safe(e.vendor_name); break;
        case 'invoice_number': v = safe(e.invoice_number); break;
        case 'invoice_date':   v = formatDate(e.invoice_date, fmt); break;
        case 'due_date':       v = formatDate(e.due_date, fmt); break;
        case 'property_code':  v = safe(c.propertyCode); break;
        case 'gl_code':        v = safe(c.glCode); break;
        case 'description':    v = buildDescription(e); break;
        case 'amount':         v = formatAmount(e.total_amount); break;
        case 'tax_amount':     v = formatAmount(e.tax_amount); break;
        case 'currency':       v = safe(e.currency || 'USD'); break;
        case 'notes':          v = ''; break;
        default:               v = '';
      }
      row[col.rawHeader] = v;
    }
    return [row];
  }

  const rows = [];
  for (const line of d.lines) {
    const row = baseRow();
    const key = distLineKey(line);
    const ov = edits[key] || {};
    const gl = ov.glCode != null ? String(ov.glCode) : line.glCode;
    const pr = ov.propertyCode != null ? String(ov.propertyCode) : line.propertyCode;

    for (const col of columns) {
      const f = col.field;
      let v = '';
      switch (f) {
        case 'vendor_name':    v = safe(e.vendor_name); break;
        case 'invoice_number': v = safe(e.invoice_number); break;
        case 'invoice_date':   v = formatDate(e.invoice_date, fmt); break;
        case 'due_date':       v = formatDate(e.due_date, fmt); break;
        case 'property_code':  v = safe(pr); break;
        case 'gl_code':        v = safe(gl); break;
        case 'description':    v = safe(line.description); break;
        case 'amount':         v = formatAmount(line.amount); break;
        case 'tax_amount':     v = ''; break;
        case 'currency':       v = safe(e.currency || 'USD'); break;
        case 'notes':          v = ''; break;
        default:               v = '';
      }
      row[col.rawHeader] = v;
    }
    rows.push(row);
  }
  return rows;
}

function safe(v) {
  if (v == null) return '';
  return String(v);
}

function formatAmount(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return '';
  return n.toFixed(2);
}

function formatDate(iso, fmt) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  const [, y, mo, d] = m;
  switch (fmt) {
    case 'MM/DD/YYYY': return `${mo}/${d}/${y}`;
    case 'DD/MM/YYYY': return `${d}/${mo}/${y}`;
    case 'YYYY-MM-DD':
    default:           return `${y}-${mo}-${d}`;
  }
}

function buildDescription(e) {
  // Default: first line item description, or invoice number + date
  if (Array.isArray(e.line_items) && e.line_items.length > 0) {
    const li = e.line_items[0];
    if (li.description) return String(li.description).slice(0, 80);
  }
  if (e.invoice_number) return `Invoice ${e.invoice_number}`;
  return '';
}

/* -------- Serialize to CSV string -------- */

export function serializeCsv(columns, rows, options = {}) {
  const headerRow = columns.map(c => c.rawHeader);
  const dataRows = rows.map(row => columns.map(c => row[c.rawHeader] ?? ''));

  const csv = Papa.unparse({ fields: headerRow, data: dataRows }, {
    quotes: true,           // always quote — safest for accounting CSV imports
    delimiter: ',',
    newline: '\r\n'         // best compatibility for Windows accounting imports
  });

  if (options.bom) {
    return '\uFEFF' + csv;
  }
  return csv;
}

/* -------- Write CSV to work folder -------- */

export async function writeCsvToFolder(workFolderHandle, filename, csvText) {
  if (!workFolderHandle) throw new Error('Work folder not set.');
  const fh = await workFolderHandle.getFileHandle(filename, { create: true });
  const w  = await fh.createWritable();
  await w.write(csvText);
  await w.close();
  return filename;
}

export function buildOutputFilename(prefix = 'invoices') {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `${prefix}-${stamp}.csv`;
}
